// CRM Contact Detail — view contact with interactions + linked items
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Mail, Phone, Building2, Tag } from 'lucide-react'
import { getCrmContact, getCrmInteractions, getCrmRelevantItems } from '../../../services/api'
import type { CrmContact, CrmInteraction, CrmRelevantItem } from '../../../types'
import InteractionTimeline from '../../../components/crm/InteractionTimeline'
import InteractionForm from '../../../components/crm/InteractionForm'
import RelevantItemsList from '../../../components/crm/RelevantItemsList'
import PageHeader from '../../../components/ui/PageHeader'
import CrmLoadError from '../../../components/crm/CrmLoadError'

export default function ContactDetail() {
  const { projectId, contactId } = useParams<{ projectId: string; contactId: string }>()
  const [contact, setContact] = useState<CrmContact | null>(null)
  const [interactions, setInteractions] = useState<CrmInteraction[]>([])
  const [relevantItems, setRelevantItems] = useState<CrmRelevantItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [showInteractionForm, setShowInteractionForm] = useState(false)
  const [tab, setTab] = useState<'interactions' | 'items'>('interactions')

  useEffect(() => {
    if (!projectId || !contactId) return
    setLoading(true)
    setLoadError(false)
    // Only a real 404 means "not found". Any other failure is a load error
    // with Retry, never "Contact not found" (UX-038).
    Promise.all([
      getCrmContact(contactId, projectId).catch((err) => {
        if (err?.response?.status === 404) return null
        throw err
      }),
      getCrmInteractions(projectId, contactId),
      getCrmRelevantItems(projectId, contactId),
    ]).then(([c, i, r]) => {
      setContact(c)
      setInteractions(i?.interactions || [])
      setRelevantItems(r?.relevant_items || [])
    }).catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }, [projectId, contactId, reloadKey])

  if (loading) return <div className="animate-pulse h-64 bg-sky-500/10 rounded-lg" />
  if (loadError) return <CrmLoadError what="this contact" onRetry={() => setReloadKey((k) => k + 1)} />
  if (!contact) return <p className="text-sm text-gray-500">Contact not found</p>

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Link to={`/project/${projectId}/crm/contacts`} className="mt-1 shrink-0 text-gray-500 hover:text-gray-600"><ArrowLeft size={18} /></Link>
        <div className="flex flex-1 items-start gap-4 min-w-0">
          <div className="mt-1 h-10 w-10 shrink-0 bg-sky-500/10 rounded-full flex items-center justify-center text-sm font-bold text-sky-700">
            {contact.first_name?.[0]}{contact.last_name?.[0]}
          </div>
          <div className="min-w-0 flex-1">
            <PageHeader
              title={`${contact.first_name} ${contact.last_name}`}
              description={contact.title ? `${contact.title}${contact.role ? ` — ${contact.role}` : ''}` : undefined}
              helpKey="crm"
              status={contact.source === 'author-conversion' ? { label: 'Converted from author', tone: 'warning' } : undefined}
            />
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="light-card p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {contact.email && <div className="flex items-center gap-2 text-sm text-gray-600"><Mail size={14} className="text-gray-500" /> {contact.email}</div>}
        {contact.phone && <div className="flex items-center gap-2 text-sm text-gray-600"><Phone size={14} className="text-gray-500" /> {contact.phone}</div>}
        {contact.organisation_name && <div className="flex items-center gap-2 text-sm text-gray-600"><Building2 size={14} className="text-gray-500" /> {contact.organisation_name}</div>}
        {contact.tags?.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Tag size={14} className="text-gray-500" />
            {contact.tags.map(t => <span key={t} className="bg-sky-500/10 px-2 py-0.5 rounded text-xs">{t}</span>)}
          </div>
        )}
        {contact.notes && <p className="text-sm text-gray-600 col-span-2">{contact.notes}</p>}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-sky-200/50">
        <button onClick={() => setTab('interactions')}
          className={`pb-2 text-sm font-medium border-b-2 ${tab === 'interactions' ? 'border-sky-500 text-sky-600' : 'border-transparent text-gray-500'}`}>
          Interactions ({interactions.length})
        </button>
        <button onClick={() => setTab('items')}
          className={`pb-2 text-sm font-medium border-b-2 ${tab === 'items' ? 'border-sky-500 text-sky-600' : 'border-transparent text-gray-500'}`}>
          Linked Items ({relevantItems.length})
        </button>
      </div>

      {tab === 'interactions' && (
        <div>
          <button onClick={() => setShowInteractionForm(true)}
            className="mb-4 text-sm text-sky-600 hover:text-sky-700 font-medium">+ Add Interaction</button>
          <InteractionTimeline interactions={interactions} />
          {showInteractionForm && projectId && contactId && (
            <InteractionForm projectId={projectId} targetType="contact" targetId={contactId}
              onClose={() => setShowInteractionForm(false)}
              onCreated={(i) => { setInteractions([i, ...interactions]); setShowInteractionForm(false) }} />
          )}
        </div>
      )}

      {tab === 'items' && <RelevantItemsList items={relevantItems} />}
    </div>
  )
}
